import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Grid } from '@mui/material';
import { Survey } from '../../props';
import {
    asyncCreateSurvey,
    asyncUpdateSurvey,
} from '../../../../../../../services/survey.service';
import { ToastSwal } from '../../../../../../components/general/toastSwals/toastSwal';
import { typesRedux } from '../../../../../../../shared/types/typesRedux';
import { SURVEYS_EDIT } from '../../../../../../../routes/paths';
import FieldText from '../../../../../../components/general/fields/fieldText';

interface Props {
    surveyInfo?: Survey;
    idSurvey?: string;
    onSubmitSave: (value: boolean) => void;
    saveInfo: (callback: () => void) => void;
    onSave: (value: boolean) => void;
    onSaveSuccess?: () => void;
}

export const SurveyInformation = ({
    surveyInfo,
    idSurvey,
    onSubmitSave,
    saveInfo,
    onSave,
    onSaveSuccess,
}: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const formik = useFormik<Survey>({
        initialValues: {
            code: surveyInfo?.code || '',
            title: surveyInfo?.title || '',
            description: surveyInfo?.description || '',
            advisor_script: surveyInfo?.advisor_script || '',
        },
        enableReinitialize: true,
        validationSchema: yup.object({
            code: yup.string().required('El código es requerido'),
            title: yup.string().required('El título es requerido'),
            description: yup.string().required('La descripción es requerida'),
            advisor_script: yup
                .string()
                .required('El guion del asesor es requerido')
                .min(
                    50,
                    'El guion del asesor debe tener al menos 50 caracteres'
                )
                .max(
                    5000,
                    'El guion del asesor no puede exceder 5000 caracteres'
                ),
        }),
        onSubmit: async (values) => {
            // Si estamos editando, verificar si hubo cambios
            if (idSurvey) {
                const hasChanges =
                    values.code !== surveyInfo?.code ||
                    values.title !== surveyInfo?.title ||
                    values.description !== surveyInfo?.description ||
                    values.advisor_script !== surveyInfo?.advisor_script;

                // Si no hay cambios, solo pasar al siguiente step
                if (!hasChanges) {
                    dispatch({ type: typesRedux.nextStepper });
                    return;
                }
            }

            onSubmitSave(true);

            try {
                let res: any;
                if (idSurvey) {
                    res = await asyncUpdateSurvey(idSurvey, values);
                } else {
                    res = await asyncCreateSurvey(values);
                }

                if (res) {
                    ToastSwal.fire({
                        text: `¡Felicidades! la encuesta se ${
                            idSurvey ? 'actualizó' : 'creó'
                        } satisfactoriamente.`,
                        icon: 'success',
                        timer: 3000,
                        position: 'center-end',
                    });

                    if (!idSurvey && res.id) {
                        navigate(`${SURVEYS_EDIT}/${res.id}`);
                        dispatch({ type: typesRedux.nextStepper });
                    } else {
                        // Recargar los datos actualizados
                        if (onSaveSuccess) {
                            await onSaveSuccess();
                        }

                        onSave(true);
                        dispatch({ type: typesRedux.nextStepper });
                        setTimeout(() => {
                            onSave(false);
                        }, 100);
                    }
                }
            } catch (error) {
                ToastSwal.fire({
                    text: `Lo sentimos, no se pudo ${
                        idSurvey ? 'actualizar' : 'crear'
                    } la encuesta. Por favor, intenta nuevamente.`,
                    icon: 'error',
                    timer: 3000,
                    position: 'center-end',
                });
            } finally {
                onSubmitSave(false);
            }
        },
    });

    useEffect(() => {
        saveInfo(() => formik.handleSubmit);
    }, []);

    return (
        <Grid
            container
            spacing={{ xs: 1.5, md: 1.5 }}
            columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
            pt="20px"
        >
            <Grid item xs={12} sm={6}>
                <FieldText
                    label="Código"
                    name="code"
                    size="small"
                    required
                    onBlur={formik.handleBlur}
                    touched={formik.touched.code}
                    errors={formik.errors.code}
                    value={formik.values.code}
                    onChange={formik.handleChange}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FieldText
                    label="Título"
                    name="title"
                    size="small"
                    required
                    onBlur={formik.handleBlur}
                    touched={formik.touched.title}
                    errors={formik.errors.title}
                    value={formik.values.title}
                    onChange={formik.handleChange}
                />
            </Grid>
            <Grid item xs={12}>
                <FieldText
                    label="Descripción"
                    name="description"
                    size="small"
                    rows={3}
                    required
                    onBlur={formik.handleBlur}
                    touched={formik.touched.description}
                    errors={formik.errors.description}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                />
            </Grid>
            <Grid item xs={12}>
                <FieldText
                    label="Guion del Asesor"
                    name="advisor_script"
                    size="small"
                    rows={6}
                    required
                    placeholder="Ejemplo: Buenos días/tardes [Nombre del Cliente], mi nombre es [Nombre Asesor] y lo contacto de Previsora. El motivo de mi llamada es realizar una breve encuesta de satisfacción sobre el servicio que usted ha recibido. ¿Cuenta con unos minutos para responder algunas preguntas?"
                    onBlur={formik.handleBlur}
                    touched={formik.touched.advisor_script}
                    errors={formik.errors.advisor_script}
                    value={formik.values.advisor_script}
                    onChange={formik.handleChange}
                />
            </Grid>
        </Grid>
    );
};
